import type { Column } from '@talos/kyoko';
import type { ICellRendererParams } from 'ag-grid-community';
import { useMemo } from 'react';

import { createColorBarCellRenderer } from '../../../components/ColorBar';

export interface TotalCostRiskRow {
  name: string;
  value: number;
  dataTestId?: string;
}

export function useTotalCostRiskColumns({
  maxValue,
  colors,
}: {
  maxValue: number | null;
  colors: {
    spreadCost: string;
    tempCost: string;
    risk: string;
  };
}) {
  return useMemo(() => {
    const spreadCostRenderer = createColorBarCellRenderer<TotalCostRiskRow>({
      field: 'value',
      color: colors.spreadCost,
      maxValue,
      unit: 'BPS',
    });
    const tempCostRenderer = createColorBarCellRenderer<TotalCostRiskRow>({
      field: 'value',
      color: colors.tempCost,
      maxValue,
      unit: 'BPS',
    });
    const riskRenderer = createColorBarCellRenderer<TotalCostRiskRow>({
      field: 'value',
      color: colors.risk,
      maxValue,
      unit: 'BPS',
    });

    const valueCellRenderer = (params: ICellRendererParams<TotalCostRiskRow>) => {
      if (!params.data) {
        return null;
      }
      switch (params.data.name) {
        case 'Spread Cost':
          return spreadCostRenderer(params);
        case 'Temp. Cost':
          return tempCostRenderer(params);
        case 'Risk':
          return riskRenderer(params);
        default:
          return spreadCostRenderer(params);
      }
    };

    const columns: Column[] = [
      {
        type: 'custom',
        field: 'name',
        title: 'ANALYTIC',
        maxWidth: 170,
        flex: 1,
        frozen: true,
        suppressHeaderMenuButton: true,
        params: {
          cellRenderer: ({ value }: { value: string }) => {
            return <>{value}</>;
          },
        },
      },
      {
        type: 'custom',
        field: 'value',
        title: 'INTERVAL',
        flex: 2,
        frozen: true,
        suppressHeaderMenuButton: true,
        params: {
          headerClass: 'ag-right-aligned-header',
          cellClass: 'ag-right-aligned-cell',
          cellRenderer: valueCellRenderer,
        },
      },
    ];
    return columns;
  }, [maxValue, colors]);
}
